/**
 * Firebase Initialization
 * =======================
 * Single source for all Firebase service instances.
 * Imported everywhere — initialized exactly ONCE.
 *
 * Security notes:
 *  - Config values here are PUBLIC (they identify the project, not authenticate).
 *    Real security is in Firestore Rules + Storage Rules + Cloud Functions.
 *  - NEVER put service account keys or secret tokens here.
 *  - All env vars are prefixed VITE_ so Vite bundles them safely.
 */

import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator }               from "firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator }         from "firebase/storage";
import { getFunctions, connectFunctionsEmulator }     from "firebase/functions";

// ─── CONFIG ──────────────────────────────────────────────────────────────────
// All values come from environment variables injected at build time.
// Copy .env.example → .env.local and fill in your Firebase project values.
const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
};

// ─── SINGLETON INIT ───────────────────────────────────────────────────────────
// Guard against double-initialization (hot reload / test environments)
const app: FirebaseApp =
  getApps().length > 0 ? getApps()[0]! : initializeApp(firebaseConfig);

// ─── FIRESTORE ────────────────────────────────────────────────────────────────
// Use persistent cache with multi-tab support for offline-first UX.
// Falls back gracefully when IndexedDB is unavailable.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const auth = getAuth(app);

// ─── STORAGE ──────────────────────────────────────────────────────────────────
export const storage = getStorage(app);

// ─── FUNCTIONS ────────────────────────────────────────────────────────────────
export const functions = getFunctions(app, "us-central1");

// ─── EMULATOR CONNECTIONS (development only) ──────────────────────────────────
// Set VITE_USE_EMULATOR=true in .env.local to use local Firebase emulators.
// This ensures dev/test traffic never touches production data.
if (import.meta.env.VITE_USE_EMULATOR === "true") {
  connectAuthEmulator(auth,      "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db,   "localhost", 8080);
  connectStorageEmulator(storage,"localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
  console.info("[AdhviBooks] 🔧 Using Firebase emulators");
}

export { app };
